import { Edit, Delete, AccessTime, Repeat } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Paper, Popover, Stack, Typography, Chip } from '@mui/material';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { scheduleStart, scheduleEnd } from '@sophocles/entities/RelaySchedule';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { getRelayBaseColor } from '@sophocles/views/sites/utils/calendarColors';
import { recurrenceLabel } from '@sophocles/views/sites/utils/recurrenceLabel';

// Helper function to format timing display
const formatTimingDisplay = (timing, user, nextSunrise, nextSunset, t) => {
  if (!timing) return '';

  const { type, time, offset = 0 } = timing;

  if (type === 'time') {
    return scheduleStart(false, time, 'time', 0, nextSunrise, nextSunset, user, t);
  }

  return scheduleStart(false, null, type, offset, nextSunrise, nextSunset, user, t);
};

function CalendarEventPopover({
  open,
  anchorEl,
  event,
  occurrenceDate,
  onClose,
  onEdit,
  onDelete,
  nextSunrise,
  nextSunset,
  mouseX,
  mouseY,
  disabled = false,
}) {
  const { t } = useTranslation();
  const user = useSelector(getAuthUserSelector);

  if (!event) return null;

  const isRecurring = Boolean(event.recurrence?.parsed);

  // Format timing displays
  const onTime = event.on_timing ? formatTimingDisplay(event.on_timing, user, nextSunrise, nextSunset, t) : '';
  const offTime = event.off_timing ? formatTimingDisplay(event.off_timing, user, nextSunrise, nextSunset, t) : '';

  // Get recurrence description
  const recurrenceDescription = recurrenceLabel(event.recurrence?.parsed, t);

  // Format event date - prefer the specific occurrence date over the series range
  let eventDate = '';
  try {
    if (occurrenceDate) {
      eventDate = format(parseISO(occurrenceDate), 'EEE, MMM d, yyyy');
    } else if (event.start_date && event.end_date) {
      eventDate = `${format(parseISO(event.start_date), 'EEE, MMM d, yyyy')} - ${format(parseISO(event.end_date), 'EEE, MMM d, yyyy')}`;
    } else if (event.start_date) {
      eventDate = format(parseISO(event.start_date), 'EEE, MMM d, yyyy');
    } else {
      eventDate = t('calendar.noDateAvailable', 'Date not available');
    }
  } catch (error) {
    console.warn('Error formatting event date:', error);
    eventDate = t('calendar.invalidDate', 'Invalid date');
  }

  // Get relay info with better fallback logic
  const relayInfo = event.relay || {};
  const relayNumber = relayInfo.relay_number || event.relay_number || event.relay_id || 1;
  const relayName = relayInfo.name || event.relay_name || `Relay ${relayNumber}`;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      anchorReference={mouseX !== null && mouseY !== null ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={
        mouseX !== null && mouseY !== null
          ? {
              top: mouseY,
              left: mouseX,
            }
          : undefined
      }
      disableScrollLock={true}
      slotProps={{
        paper: {
          elevation: 8,
          sx: {
            mt: 1,
            maxWidth: 350,
            borderRadius: 'var(--radius-lg)',
            zIndex: 10000,
          },
        },
      }}
      sx={{
        zIndex: 10000,
      }}
    >
      <Paper sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {event.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {eventDate}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
              ×
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        {/* Event Details */}
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Timing Information */}
            {(onTime || offTime) && (
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {onTime && offTime ? `${onTime} - ${offTime}` : onTime || offTime}
                </Typography>
              </Stack>
            )}

            {/* Relay Information */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: getRelayBaseColor(relayNumber),
                  border: 1,
                  borderColor: 'divider',
                }}
              />
              <Typography variant="body2">{relayName}</Typography>
            </Stack>

            {/* Recurrence Information */}
            {isRecurring && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Repeat sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">{recurrenceDescription}</Typography>
              </Stack>
            )}

            {/* Priority */}
            {/* {event.priority && event.priority !== 5 && (
              <Chip
                label={`${t('calendar.eventDialog.priority', 'Priority')}: ${event.priority}`}
                size="small"
                variant="outlined"
                sx={{ alignSelf: 'flex-start' }}
              />
            )} */}

            {/* Description */}
            {event.description && (
              <Typography variant="body2" color="text.secondary">
                {event.description}
              </Typography>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Action Buttons */}
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              startIcon={<Delete />}
              onClick={() => {
                onClose();
                onDelete(event.id, isRecurring);
              }}
              color="error"
              size="small"
              disabled={disabled}
            >
              {t('actions.delete', 'Delete')}
            </Button>
            <Button
              startIcon={<Edit />}
              onClick={() => {
                onClose();
                onEdit(event.id, isRecurring);
              }}
              variant="contained"
              size="small"
              disabled={disabled}
            >
              {t('actions.edit', 'Edit')}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Popover>
  );
}

export default CalendarEventPopover;
